import { Phone, Facebook, Instagram, Youtube } from "lucide-react";
import { Link } from "react-router-dom";

const socials = [
  { label: "Facebook", href: "https://www.facebook.com/happytiddlers/", Icon: Facebook },
  { label: "Instagram", href: "http://instagram.com/tiddlertastic", Icon: Instagram },
  { label: "YouTube", href: "https://www.youtube.com/@happytiddlers1026", Icon: Youtube },
];

const PUB = process.env.PUBLIC_URL;
const partners = [
  { name: "Mum under Construction", logo: `${PUB}/partners/mum-under-construction.jpg`, href: "https://www.mumunderconstructionbarton.co.uk/about-us/" },
  { name: "Pivot 1st", logo: `${PUB}/partners/pivot1st.png`, href: "https://www.pivot1st.co.uk" },
  { name: "YAWN Life", logo: `${PUB}/partners/yawn-life.png`, href: null },
];

const cols = [
  { title: "Explore", links: [
    { label: "Home", to: "/" },
    { label: "Classes", to: "/classes" },
    { label: "Timetable", to: "/timetable" },
    { label: "Term Dates", to: "/term-dates" },
    { label: "What to Expect", to: "/how-it-works" },
    { label: "About Us", to: "/about" },
    { label: "Meet the Team", to: "/team" },
    { label: "Blog", to: "/blog" },
  ]},
  { title: "Classes", links: [
    { label: "Baby Swimming", to: "/classes/baby-swimming" },
    { label: "Tiddler & Pre-School", to: "/classes/toddler-swimming" },
    { label: "Children's Lessons", to: "/classes/childrens-swimming" },
    { label: "Pricing", to: "/pricing" },
  ]},
  { title: "Visit", links: [
    { label: "Barton-le-Clay", to: "/contact" },
    { label: "Bedfordshire", to: "/contact" },
    { label: "Mon–Sun classes", to: "/timetable" },
  ]},
];

export default function Footer() {
  return (
    <footer className="bg-[#0A2540] border-t border-[#FF7A1A]/15" data-testid="footer">
      <div className="container mx-auto px-6 md:px-12 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12">
          <div>
            <Link to="/" className="flex items-center gap-3 mb-5">
              <img
                src={`${process.env.PUBLIC_URL}/logo.png`}
                alt="Happy Tiddlers Swim School logo"
                className="h-16 w-16 object-contain bg-white rounded-2xl p-1"
              />
              <span className="font-logo text-2xl text-white tracking-wide leading-none">
                Happy <span className="text-[#38BDF8]">Tiddlers</span>
              </span>
            </Link>
            <p className="text-[#94A3B8] leading-relaxed max-w-xs">
              An exclusive, private STA 'Swim Star' swim school in Barton-le-Clay — building happy, confident swimmers since 2014.
            </p>
            <a href="tel:01582883820" className="inline-flex items-center gap-2 mt-5 text-white font-semibold hover:text-[#38BDF8] transition-colors">
              <Phone size={16} /> 01582 883820
            </a>
            <div className="flex items-center gap-3 mt-6" data-testid="footer-socials">
              {socials.map(({ label, href, Icon }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  data-testid={`footer-social-${label.toLowerCase()}`}
                  className="grid place-items-center w-10 h-10 rounded-xl bg-white/10 text-white hover:bg-[#FF7A1A] hover:-translate-y-0.5 transition-all duration-300"
                >
                  <Icon size={18} />
                </a>
              ))}
            </div>
          </div>

          {cols.map((c) => (
            <div key={c.title}>
              <h4 className="font-heading font-semibold text-white mb-5">{c.title}</h4>
              <ul className="space-y-3">
                {c.links.map((l) => (
                  <li key={l.label}>
                    <Link to={l.to} className="text-[#94A3B8] hover:text-[#38BDF8] transition-colors">{l.label}</Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-14 pt-8 border-t border-white/10" data-testid="footer-partners">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#64748B] mb-4">Proud to partner with</p>
          <div className="flex flex-wrap items-center gap-3">
            {partners.map(({ name, href, logo }) => {
              const chip = (
                <span className="grid place-items-center h-14 px-5 rounded-2xl bg-white shadow-sm">
                  <img src={logo} alt={`${name} logo`} loading="lazy" className="max-h-9 w-auto object-contain" />
                </span>
              );
              return href ? (
                <a
                  key={name}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={name}
                  data-testid={`footer-partner-${name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/-$/, "")}`}
                  className="transition-transform duration-300 hover:-translate-y-0.5"
                >
                  {chip}
                </a>
              ) : (
                <span key={name} data-testid={`footer-partner-${name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/-$/, "")}`}>
                  {chip}
                </span>
              );
            })}
          </div>
        </div>

        <div className="mt-14 pt-8 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-[#64748B] text-sm">© {new Date().getFullYear()} Happy Tiddlers Swim School. All rights reserved.</p>
          <div className="flex items-center gap-5">
            <Link to="/privacy" className="text-sm text-[#94A3B8] hover:text-[#38BDF8] transition-colors" data-testid="footer-privacy-link">Privacy Policy</Link>
            <Link to="/terms" className="text-sm text-[#94A3B8] hover:text-[#38BDF8] transition-colors" data-testid="footer-terms-link">Terms &amp; Conditions</Link>
            <a href="https://share.google/LEsF23vYmK87fVZXX" target="_blank" rel="noopener noreferrer" className="text-sm text-[#94A3B8] hover:text-[#38BDF8] transition-colors" data-testid="footer-reviews-link">Google Reviews</a>
            <a href="mailto:hello@happytiddlers.co.uk" className="text-sm text-[#94A3B8] hover:text-[#38BDF8] transition-colors">
              hello@happytiddlers.co.uk
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
